import React from "react";
import confetti from "canvas-confetti";
import { animated, useSpring, config } from "@react-spring/web";
import { Button, Typography, TextField, CircularProgress } from "@material-ui/core";

import { PublicLinkLocationType, LinkLocationType, BookingLocationType } from "@clockwise/schema";
import { BookedSlot } from "@ui/scheduling-link/scheduling-link";

import { ConfirmationTitle } from "./ConfirmationTitle";
import { EventSummary } from "./event-summary";
import { Notification } from "./notification";
import { trim } from "lodash";
import { OwnerBanner } from "@ui/scheduling-links/OwnerBanner";
import { getEditSchedulingLinkPath, getSchedulingLinkUrl } from "@util/scheduling.util";
import { useRouter } from "found";
import { paths } from "@clockwise/client-commons/src/constants/site";

export enum ConfirmationType {
  Book = "Book",
  Cancel = "Cancel",
  Reschedule = "Reschedule",
}

export interface SchedulingConfirmationProps {
  meetingSettings: {
    id: string;
    name: string;
    description: string | null;
    durationMinutes: number;
    locationType: PublicLinkLocationType | LinkLocationType | BookingLocationType;
  };
  linkName: string;
  slug: string;
  isOwner: boolean;
  owner: {
    givenName: string | null;
    familyName: string | null;
    externalImageUrl: string | null;
  };
  prevSelectedTime?: string;
  selectedTime: BookedSlot;
  eventDescription?: string | null;
  variant: ConfirmationType;
  completed: boolean;
  cancelRescheduleReason?: string;
  showConfetti?: boolean;
  banner: React.ReactNode;
  onCancel?: (reason: string) => Promise<void>;
}

const ANIMATION_DURATION = 5500;
const MAX_REASON_LENGTH = 400;

export const SchedulingConfirmation = ({
  meetingSettings: { id, name: eventName, durationMinutes: duration, locationType, description },
  linkName,
  slug,
  isOwner,
  owner,
  prevSelectedTime,
  selectedTime,
  variant,
  completed,
  cancelRescheduleReason,
  showConfetti = true,
  banner,
  onCancel,
}: SchedulingConfirmationProps) => {
  const { router } = useRouter();
  const [cancelReason, setCancelReason] = React.useState("");
  const [cancelling, setCancelling] = React.useState(false);
  const [deleteFailed, setDeleteFailed] = React.useState(false);
  const reason = cancelRescheduleReason || selectedTime.reason;

  const onEditLink = () => router.push(getEditSchedulingLinkPath(slug));
  const onBack = () => router.push(paths.schedulingLinks);

  const confirmationStyles = useSpring({
    from: { opacity: 0 },
    to: { opacity: 1 },
    delay: 200,
    config: config.slow,
  });

  // requestAnimationFrame returns a request id that we pass
  // to cancelAnimationFrame when we clean up the effect below
  const animationRequestRef = React.useRef<number | undefined>();

  const animate = React.useCallback(
    (timestamp: number) => {
      if (!showConfetti) {
        if (animationRequestRef.current) {
          cancelAnimationFrame(animationRequestRef.current);
        }
        return;
      }
      // launch a few from the left edge
      void confetti({
        particleCount: 20,
        angle: 60,
        spread: 55,
        origin: { x: 0 },
        disableForReducedMotion: true,
      });

      // and launch a few from the right edge
      void confetti({
        particleCount: 20,
        angle: 120,
        spread: 55,
        origin: { x: 1 },
        disableForReducedMotion: true,
      });

      if (timestamp < ANIMATION_DURATION) {
        animationRequestRef.current = requestAnimationFrame(animate);
      }
    },
    [showConfetti],
  );

  React.useEffect(() => {
    if ([ConfirmationType.Book, ConfirmationType.Reschedule].includes(variant) && completed) {
      animationRequestRef.current = requestAnimationFrame(animate);
    }

    return () => {
      if (animationRequestRef.current) {
        cancelAnimationFrame(animationRequestRef.current);
      }
    };
  }, [animate]);

  return (
    <animated.div className="cw-flex cw-justify-center" style={confirmationStyles}>
      <div className="cw-flex cw-flex-col cw-justify-start cw-w-full cw-max-w-[600px] cw-mx-5 cw-mt-9 cw-gap-5 sm:cw-mt-20 cw-mb-[50px] sm:cw-mb-10">
        {onEditLink && onBack && isOwner && (
          <OwnerBanner
            isOwner={isOwner}
            link={{ linkUrl: getSchedulingLinkUrl(linkName, slug), meetingSettings: { id, slug } }}
            onEditLink={onEditLink}
            onBack={onBack}
          />
        )}
        <div cw-id="booking-confirmation" className="cw-flex cw-flex-col cw-gap-5">
          <ConfirmationTitle
            owner={owner}
            variant={variant}
            completed={completed}
            reasonForChange={reason}
          />
          <EventSummary
            title={eventName}
            duration={duration}
            prevDateTime={prevSelectedTime}
            dateTime={selectedTime.bookedDateOrDateTime}
            timeZone={selectedTime.bookedTimeZone}
            location={locationType}
            description={description}
            variant={variant}
            cancelled={variant === ConfirmationType.Cancel && completed}
          >
            {variant === ConfirmationType.Cancel && !completed && (
              <div className="cw-flex cw-flex-col cw-gap-4 cw-mt-3">
                <div className="cw-font-brand cw-font-bold cw-leading-tight cw-text-24 cw-text-dusk-darker">
                  Reason for cancellation
                </div>
                <TextField
                  variant="filled"
                  InputProps={{
                    disableUnderline: true,
                    className: "cw-rounded",
                  }}
                  inputProps={{
                    className: "cw-p-3",
                  }}
                  fullWidth
                  placeholder="Reason"
                  aria-label="reason for cancelling"
                  value={cancelReason}
                  onChange={(e) => {
                    if (e.target.value.length > MAX_REASON_LENGTH) {
                      return;
                    }
                    setCancelReason(e.target.value);
                    setDeleteFailed(false);
                  }}
                />
                <div className="cw-flex cw-flex-col cw-gap-2">
                  <div className="cw-flex cw-items-center cw-gap-4">
                    <Button
                      onClick={async () => {
                        setCancelling(true);
                        setDeleteFailed(false);
                        try {
                          await onCancel?.(trim(cancelReason));
                          setCancelling(false);
                        } catch {
                          setCancelling(false);
                          setDeleteFailed(true);
                        }
                      }}
                      variant="contained"
                      color="primary"
                      disabled={cancelling}
                      aria-label="cancel meeting"
                    >
                      Cancel Meeting
                    </Button>
                    {cancelling && <CircularProgress size={18} color="primary" />}
                  </div>
                  {deleteFailed && (
                    <Typography variant="body2" color="error">
                      There was an error cancelling the meeting. Please try again.
                    </Typography>
                  )}
                </div>
              </div>
            )}
          </EventSummary>
          {[ConfirmationType.Book, ConfirmationType.Reschedule].includes(variant) && completed && (
            <Notification
              sentiment="success"
              message="A meeting invite has been sent to your email"
            />
          )}
        </div>
        {banner}
      </div>
    </animated.div>
  );
};
